import { type ReactNode, useCallback, useEffect, useState } from 'react';

import { fontStyles } from '@/styles/typography';

import { classNames } from '@/utils/classnames';

import type { ChartPlot } from './types';

const POINT_GAP = 12;

type ChartTooltipProps = {
  children: ReactNode;
  footer?: ReactNode;
  x: number;
  y: number;
  plot: ChartPlot;
};

export const ChartTooltip = ({ children, footer, x, y, plot }: ChartTooltipProps) => {
  const [element, setElement] = useState<HTMLDivElement | null>(null);
  const [size, setSize] = useState({ width: 0, height: 0 });

  const tooltipRef = useCallback((node: HTMLDivElement | null) => {
    setElement(node);

    if (node) {
      const { width, height } = node.getBoundingClientRect();
      setSize({ width, height });
    }
  }, []);

  useEffect(() => {
    if (!element) return undefined;

    const observer = new ResizeObserver(() => {
      // Use the border box so padding and borders are included in collision positioning.
      const { width, height } = element.getBoundingClientRect();
      setSize({ width, height });
    });

    observer.observe(element);
    return () => observer.disconnect();
  }, [element]);

  const { width, height } = size;
  const isPointOnLeft = x <= plot.left + plot.width / 2;
  const preferredLeft = isPointOnLeft ? x + POINT_GAP : x - width - POINT_GAP;

  const left = Math.min(
    Math.max(preferredLeft, plot.left),
    Math.max(plot.right - width, plot.left),
  );
  const top = Math.min(
    Math.max(y - height / 2, plot.top),
    Math.max(plot.bottom - height, plot.top),
  );

  const style = { left, top, maxWidth: plot.width, maxHeight: plot.height };

  return (
    <div
      ref={tooltipRef}
      role="tooltip"
      style={style}
      className={classNames(
        'pointer-events-none absolute z-20 flex min-w-36 flex-col gap-1.5',
        'overflow-hidden whitespace-nowrap rounded-lg border border-token-gray-300',
        'bg-special-background-tooltip px-3 py-2 shadow-25',
      )}
    >
      {children}
      {footer != null && (
        <span className={fontStyles({ variant: 'CAPTION', class: 'text-token-gray-700' })}>
          {footer}
        </span>
      )}
    </div>
  );
};

type ChartTooltipRowProps = {
  label: ReactNode;
  value: ReactNode;
  valueClass?: string;
};

export const ChartTooltipRow = ({ label, value, valueClass }: ChartTooltipRowProps) => (
  <div className="flex items-center justify-between gap-4">
    <span className={fontStyles({ variant: 'CAPTION', class: 'text-token-gray-700' })}>
      {label}
    </span>
    <span className={fontStyles({ variant: 'LABEL_SM', class: valueClass })}>{value}</span>
  </div>
);
